import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
    FaArrowLeft,
    FaUser,
    FaCheckCircle,
    FaTimesCircle,
    FaHourglassHalf,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaBuilding,
    FaCalendar,
    FaImage,
    FaVideo,
    FaPlay,
    FaClock,
    FaGlobe,
    FaExclamationTriangle
} from "react-icons/fa";
import { toast } from "react-toastify";

const UserAdDetailView = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [adTypeFilter, setAdTypeFilter] = useState("all");
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [walletBalance, setWalletBalance] = useState(0);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});

    useEffect(() => {
        fetchUserDetails();
    }, [userId]);

    const fetchUserDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/admin/get/user-ad-details/${userId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            setUser(response.data.user);
            setAds(response.data.data || []);
            setTotalEarnings(response.data.totalEarnings || 0);
            setWalletBalance(response.data.walletBalance || 0);
        } catch (error) {
            console.error("Error fetching user details:", error);
            toast.error("Failed to fetch user details");
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "completed":
                return "bg-green-50 text-green-700 border-green-200";
            case "accepted":
                return "bg-yellow-50 text-yellow-700 border-yellow-200";
            case "submitted":
                return "bg-blue-50 text-blue-700 border-blue-200";
            case "rejected":
                return "bg-red-50 text-red-700 border-red-200";
            default:
                return "bg-gray-50 text-gray-700 border-gray-200";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "completed":
                return <FaCheckCircle className="text-green-600" />;
            case "accepted":
                return <FaHourglassHalf className="text-yellow-600" />;
            case "submitted":
                return <FaClock className="text-blue-600" />;
            case "rejected":
                return <FaTimesCircle className="text-red-600" />;
            default:
                return <FaHourglassHalf className="text-gray-600" />;
        }
    };

    const filteredAds = ads.filter(ad => {
        const matchesStatus = filter === "all" || ad.userStatus === filter;
        const matchesType = adTypeFilter === "all" ||
            (adTypeFilter === "paid" && ad.isPaid) ||
            (adTypeFilter === "free" && !ad.isPaid);
        return matchesStatus && matchesType;
    });

    const stats = {
        total: ads.length,
        completed: ads.filter(a => a.userStatus === "completed").length,
        accepted: ads.filter(a => a.userStatus === "accepted").length,
        submitted: ads.filter(a => a.userStatus === "submitted").length,
        rejected: ads.filter(a => a.rejectNote).length
    };

    const toggleDescription = (adId) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [adId]: !prev[adId]
        }));
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-black text-gray-800 mb-4">User Not Found</h2>
                    <button
                        onClick={() => navigate("/admin/user-ads-tracking")}
                        className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50/50 p-2 sm:p-4 lg:p-6">
            {/* Back Button */}
            <div className="max-w-7xl mx-auto mb-3">
                <button
                    onClick={() => navigate("/admin/user-ads-tracking")}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all font-black text-[10px] uppercase tracking-wider text-gray-700 hover:text-indigo-600 border border-gray-100"
                >
                    <FaArrowLeft />
                    <span>Back to Tracking</span>
                </button>
            </div>

            {/* User Profile Card */}
            <div className="max-w-7xl mx-auto mb-3">
                <div className="bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-800 rounded-2xl p-4 sm:p-5 text-white shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" />

                    <div className="relative z-10">
                        <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/10 shrink-0">
                                <FaUser className="text-2xl text-white" />
                            </div>
                            <div className="flex-1 text-center md:text-left">
                                <h1 className="text-xl sm:text-2xl font-black mb-1">{user.name}</h1>
                                <div className="flex flex-wrap justify-center md:justify-start items-center gap-2">
                                    <span className="px-3 py-1 bg-white/20 rounded-full text-[10px] font-black uppercase tracking-wider backdrop-blur-md border border-white/10">
                                        {user.iinsafId}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider backdrop-blur-md border ${user.role === "Reporter"
                                        ? "bg-blue-500/30 border-blue-300/30"
                                        : "bg-purple-500/30 border-purple-300/30"
                                        }`}>
                                        {user.role}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-2.5 border border-white/10">
                                <div className="flex items-center gap-2 mb-1">
                                    <FaEnvelope className="text-white/50 text-[10px]" />
                                    <span className="text-[8px] font-black text-white/50 uppercase tracking-widest">Email</span>
                                </div>
                                <p className="text-[11px] font-bold truncate">{user.email}</p>
                            </div>
                            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-2.5 border border-white/10">
                                <div className="flex items-center gap-2 mb-1">
                                    <FaPhone className="text-white/50 text-[10px]" />
                                    <span className="text-[8px] font-black text-white/50 uppercase tracking-widest">Phone</span>
                                </div>
                                <p className="text-[11px] font-bold">{user.mobile}</p>
                            </div>
                            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-2.5 border border-white/10">
                                <div className="flex items-center gap-2 mb-1">
                                    <FaMapMarkerAlt className="text-white/50 text-[10px]" />
                                    <span className="text-[8px] font-black text-white/50 uppercase tracking-widest">Location</span>
                                </div>
                                <p className="text-[11px] font-bold truncate">{user.city}, {user.state}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="max-w-7xl mx-auto mb-3">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100/50 flex flex-col justify-center items-center text-center">
                        <div className="text-[8px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Total Ads</div>
                        <div className="text-xl font-black text-gray-800 leading-none">{stats.total}</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100/50 flex flex-col justify-center items-center text-center">
                        <div className="text-[8px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Completed</div>
                        <div className="text-xl font-black text-green-600 leading-none">{stats.completed}</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100/50 flex flex-col justify-center items-center text-center">
                        <div className="text-[8px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Active</div>
                        <div className="text-xl font-black text-yellow-500 leading-none">{stats.accepted}</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100/50 flex flex-col justify-center items-center text-center">
                        <div className="text-[8px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Submitted</div>
                        <div className="text-xl font-black text-blue-600 leading-none">{stats.submitted}</div>
                    </div>
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100/50 flex flex-col justify-center items-center text-center">
                        <div className="text-[8px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Rejected</div>
                        <div className="text-xl font-black text-red-500 leading-none">{stats.rejected}</div>
                    </div>
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-3 shadow-md border border-indigo-200 flex flex-col justify-center items-center text-center">
                        <div className="text-[8px] font-black text-indigo-100 uppercase tracking-[0.2em] mb-1">Wallet</div>
                        <div className="text-xl font-black text-white leading-none truncate w-full">₹{walletBalance.toLocaleString()}</div>
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="max-w-7xl mx-auto mb-4 flex flex-wrap gap-4 items-center">
                {/* Ad Type Filter */}
                <div className="bg-white rounded-xl p-1.5 shadow-md border border-gray-100 inline-flex gap-1.5">
                    {[
                        { value: "all", label: "All Ads" },
                        { value: "paid", label: "Paid" },
                        { value: "free", label: "Free" }
                    ].map(tab => (
                        <button
                            key={tab.value}
                            onClick={() => setAdTypeFilter(tab.value)}
                            className={`px-4 py-2 rounded-lg font-black text-[10px] uppercase tracking-wider transition-all ${adTypeFilter === tab.value
                                ? "bg-amber-500 text-white shadow-md"
                                : "text-gray-500 hover:bg-gray-50"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Status Filter */}
                <div className="bg-white rounded-xl p-1.5 shadow-md border border-gray-100 inline-flex gap-1.5">
                    {[
                        { value: "all", label: "Any Status", count: stats.total },
                        { value: "completed", label: "Completed", count: stats.completed },
                        { value: "accepted", label: "Active", count: stats.accepted },
                        { value: "submitted", label: "Submitted", count: stats.submitted }
                    ].map(tab => (
                        <button
                            key={tab.value}
                            onClick={() => setFilter(tab.value)}
                            className={`px-4 py-2 rounded-lg font-black text-[10px] uppercase tracking-wider transition-all ${filter === tab.value
                                ? "bg-indigo-600 text-white shadow-md"
                                : "text-gray-500 hover:bg-gray-50"
                                }`}
                        >
                            {tab.label} ({tab.count})
                        </button>
                    ))}
                </div>
            </div>

            {/* Ads List */}
            <div className="max-w-7xl mx-auto">
                {filteredAds.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-16 text-center">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FaExclamationTriangle className="w-12 h-12 text-gray-300" />
                        </div>
                        <h3 className="text-2xl font-black text-gray-800 mb-3 uppercase tracking-tight">
                            No Ads Found
                        </h3>
                        <p className="text-gray-500 text-lg font-medium">
                            No ads match the selected filter.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredAds.map((ad, idx) => (
                            <div key={idx} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-all">
                                <div className="flex flex-col md:flex-row">
                                    {/* Media Section */}
                                    {(ad.imageUrl || ad.videoUrl || ad.media) && (
                                        <div className="md:w-48 h-40 md:h-auto bg-gray-900 flex-shrink-0">
                                            {(ad.mediaType?.toLowerCase() === "video" || ad.videoUrl) ? (
                                                <video
                                                    src={ad.videoUrl || ad.imageUrl || ad.media}
                                                    className="w-full h-full object-cover"
                                                    controls
                                                />
                                            ) : (
                                                <img
                                                    src={ad.imageUrl || ad.videoUrl || ad.media}
                                                    alt="Ad media"
                                                    className="w-full h-full object-cover"
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* Content Section */}
                                    <div className="flex-1 p-3">
                                        {/* Header */}
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                                                    <span className="bg-gray-800 text-white text-[9px] px-2 py-0.5 rounded-md font-black shadow-sm">
                                                        #{idx + 1}
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase ${ad.mediaType?.toLowerCase() === "video" ? "bg-red-500 text-white shadow-sm" : "bg-blue-600 text-white shadow-sm"}`}>
                                                        {ad.mediaType?.toLowerCase() === "video" ? <FaVideo className="inline mr-1" /> : <FaImage className="inline mr-1" />}
                                                        {ad.adType || ad.mediaType}
                                                    </span>
                                                    <div className={`px-2 py-0.5 rounded-md border-2 ${getStatusColor(ad.userStatus)} font-black text-[9px] uppercase flex items-center gap-1`}>
                                                        {getStatusIcon(ad.userStatus)}
                                                        {ad.userStatus}
                                                    </div>
                                                    {ad.isPaid ? (
                                                        <span className="bg-amber-100 text-amber-700 text-[9px] px-2 py-0.5 rounded-md font-black border border-amber-200 flex items-center gap-1 uppercase">
                                                            💎 Paid
                                                        </span>
                                                    ) : (
                                                        <span className="bg-indigo-100 text-indigo-700 text-[9px] px-2 py-0.5 rounded-md font-black border border-indigo-200 flex items-center gap-1 uppercase">
                                                            🎁 Free
                                                        </span>
                                                    )}
                                                </div>
                                                <div
                                                    className="text-sm text-gray-700 cursor-pointer hover:text-gray-900"
                                                    onClick={() => toggleDescription(ad.adId)}
                                                >
                                                    {expandedDescriptions[ad.adId] ? ad.description : truncateText(ad.description, 100)}
                                                    {ad.description && ad.description.length > 100 && (
                                                        <span className="text-indigo-600 font-bold ml-2">
                                                            {expandedDescriptions[ad.adId] ? "Show less" : "Show more"}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Payment & Earnings Section */}
                                        {ad.payment > 0 && (
                                            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-2 border-2 border-emerald-200 mb-2">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <p className="text-[10px] font-bold text-emerald-600 uppercase">💰 Payment</p>
                                                        <p className="text-xl font-black text-emerald-700">₹{ad.payment.toFixed(2)}</p>
                                                    </div>
                                                    {ad.userStatus === "completed" && (
                                                        <div className="bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold italic tracking-tighter">
                                                            ✓ PAID
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Platforms & Duration (Combined Compact) */}
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {ad.platforms && ad.platforms.length > 0 && (
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    {ad.platforms.map((platform, idx) => (
                                                        <span key={idx} className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md text-[10px] font-black border border-purple-200 uppercase">
                                                            {platform}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {(ad.startDate || ad.endDate) && (
                                                <div className="bg-blue-50 rounded-md px-2 py-0.5 border border-blue-100 flex items-center gap-2 text-[10px]">
                                                    <span className="text-blue-600 font-black">📅 DURATION:</span>
                                                    <span className="text-gray-800 font-bold">
                                                        {ad.startDate ? new Date(ad.startDate).toLocaleDateString() : 'N/A'} - {ad.endDate ? new Date(ad.endDate).toLocaleDateString() : 'Active'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Target Info */}
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                                            {ad.targetLocation && (ad.targetLocation.state || ad.targetLocation.city) && (
                                                <div className="bg-indigo-50 rounded-lg p-2 border border-indigo-100">
                                                    <p className="text-[10px] font-bold text-indigo-400 uppercase">Target Location</p>
                                                    <p className="text-xs font-bold text-indigo-700">
                                                        {ad.targetLocation.city && ad.targetLocation.state
                                                            ? `${ad.targetLocation.city}, ${ad.targetLocation.state}`
                                                            : ad.targetLocation.state || ad.targetLocation.city}
                                                    </p>
                                                </div>
                                            )}
                                            {ad.targetAudience && (
                                                <div className="bg-purple-50 rounded-lg p-2 border border-purple-100">
                                                    <p className="text-[10px] font-bold text-purple-400 uppercase">Audience</p>
                                                    <p className="text-xs font-bold text-purple-700">{ad.targetAudience}</p>
                                                </div>
                                            )}
                                            {ad.requiredReporters && (
                                                <div className="bg-teal-50 rounded-lg p-2 border border-teal-100">
                                                    <p className="text-[10px] font-bold text-teal-400 uppercase">Required</p>
                                                    <p className="text-xs font-bold text-teal-700">{ad.requiredReporters} reporters</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Timeline */}
                                        <div className="flex flex-wrap gap-2 text-xs">
                                            {ad.acceptedAt && (
                                                <div className="bg-blue-50 rounded-lg p-2 border border-blue-100">
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <FaCalendar className="text-blue-600 text-xs" />
                                                        <span className="text-[10px] font-bold text-blue-600 uppercase">Accepted</span>
                                                    </div>
                                                    <p className="text-xs font-medium text-gray-800">
                                                        {new Date(ad.acceptedAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            )}
                                            {ad.submittedAt && (
                                                <div className="bg-yellow-50 rounded-lg p-2 border border-yellow-100">
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <FaClock className="text-yellow-600 text-xs" />
                                                        <span className="text-[10px] font-bold text-yellow-600 uppercase">Submitted</span>
                                                    </div>
                                                    <p className="text-xs font-medium text-gray-800">
                                                        {new Date(ad.submittedAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            )}
                                            {ad.completedAt && (
                                                <div className="bg-green-50 rounded-lg p-2 border border-green-100">
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <FaCheckCircle className="text-green-600 text-xs" />
                                                        <span className="text-[10px] font-bold text-green-600 uppercase">Completed</span>
                                                    </div>
                                                    <p className="text-xs font-medium text-gray-800">
                                                        {new Date(ad.completedAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            )}
                                            {ad.rejectedAt && (
                                                <div className="bg-red-50 rounded-lg p-2 border border-red-100">
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <FaTimesCircle className="text-red-600 text-xs" />
                                                        <span className="text-[10px] font-bold text-red-600 uppercase">Rejected</span>
                                                    </div>
                                                    <p className="text-xs font-medium text-gray-800">
                                                        {new Date(ad.rejectedAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Proof Details */}
                                        {ad.proof && (
                                            <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-200 mt-2">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-6 h-6 bg-indigo-600 rounded-md flex items-center justify-center">
                                                        <FaCheckCircle className="text-white text-[10px]" />
                                                    </div>
                                                    <h4 className="text-[10px] font-black text-gray-800 uppercase tracking-tight">Submission Details</h4>
                                                    {ad.proof.status && (
                                                        <span className={`ml-auto px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${ad.proof.status === 'approved'
                                                            ? 'bg-green-100 text-green-700'
                                                            : 'bg-yellow-100 text-yellow-700'
                                                            }`}>
                                                            {ad.proof.status === 'approved' ? '✓ Approved' : '⏳ Review'}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Submission Info Grid */}
                                                <div className="grid grid-cols-4 gap-2 mb-2">
                                                    {ad.proof.platform && (
                                                        <div className="bg-white rounded p-1.5 border border-gray-100">
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Platform</p>
                                                            <p className="text-[10px] font-black text-gray-800">{ad.proof.platform}</p>
                                                        </div>
                                                    )}
                                                    {ad.proof.channelName && (
                                                        <div className="bg-white rounded p-1.5 border border-gray-100">
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Channel</p>
                                                            <p className="text-[10px] font-black text-gray-800 truncate">{ad.proof.channelName}</p>
                                                        </div>
                                                    )}
                                                    {ad.proof.iinsafId && (
                                                        <div className="bg-white rounded p-1.5 border border-gray-100">
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">iinsaf ID</p>
                                                            <p className="text-[10px] font-black text-gray-800">{ad.proof.iinsafId}</p>
                                                        </div>
                                                    )}
                                                    {ad.proof.duration && (
                                                        <div className="bg-white rounded p-1.5 border border-gray-100">
                                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Duration</p>
                                                            <p className="text-[10px] font-black text-gray-800">{ad.proof.duration}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Video Link & Screenshots */}
                                                <div className="flex flex-col sm:flex-row gap-2">
                                                    {ad.proof.videoLink && (
                                                        <div className="shrink-0 mb-1">
                                                            <a
                                                                href={ad.proof.videoLink}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded font-black text-[9px] uppercase tracking-tighter shadow hover:bg-red-700 transition-all"
                                                            >
                                                                <FaPlay className="text-[8px]" /> View Video
                                                            </a>
                                                        </div>
                                                    )}
                                                    {(ad.proof.screenshot || ad.proof.completedTaskScreenshot) && (
                                                        <div className="flex gap-2 overflow-x-auto pb-1">
                                                            {ad.proof.screenshot && (
                                                                <div className="relative group shrink-0">
                                                                    <img
                                                                        src={ad.proof.screenshot}
                                                                        alt="Proof"
                                                                        className="w-14 h-14 object-cover rounded border border-gray-200 cursor-pointer shadow-sm hover:border-indigo-400"
                                                                        onClick={() => window.open(ad.proof.screenshot, '_blank')}
                                                                    />
                                                                    <p className="text-[8px] text-gray-400 mt-0.5 font-bold uppercase text-center">Main</p>
                                                                </div>
                                                            )}
                                                            {ad.proof.completedTaskScreenshot && (
                                                                <div className="relative group shrink-0">
                                                                    <img
                                                                        src={ad.proof.completedTaskScreenshot}
                                                                        alt="Proof"
                                                                        className="w-14 h-14 object-cover rounded border border-gray-200 cursor-pointer shadow-sm hover:border-indigo-400"
                                                                        onClick={() => window.open(ad.proof.completedTaskScreenshot, '_blank')}
                                                                    />
                                                                    <p className="text-[8px] text-gray-400 mt-0.5 font-bold uppercase text-center">Task</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Submitted At */}
                                                {ad.proof.submittedAt && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                                        <p className="text-xs text-gray-500">
                                                            <FaClock className="inline mr-1" />
                                                            Submitted on {new Date(ad.proof.submittedAt).toLocaleString()}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Rejection Note */}
                                        {ad.rejectNote && (
                                            <div className="bg-red-50 rounded-lg p-3 border-2 border-red-200 mt-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FaExclamationTriangle className="text-red-600" />
                                                    <h4 className="text-xs font-black text-red-800 uppercase">Rejection Note</h4>
                                                </div>
                                                <p className="text-xs text-red-700 font-medium">{ad.rejectNote}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserAdDetailView;
